import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const LogAuditUser = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  
  const ITEMS_PER_PAGE = 7;

  // Dohvaćanje logova
  const fetchAuditLogs = useCallback(async (pageNumber = 0) => {
    if (pageNumber === 0) setLoading(true);
    else setLoadingMore(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const from = pageNumber * ITEMS_PER_PAGE;
        const to = from + ITEMS_PER_PAGE - 1;

        const { data: auditData, error } = await supabase
          .from('audit_logs')
          .select('*')
          .eq('target_uuid_text', user.id) 
          .order('created_at', { ascending: false })
          .range(from, to);

        if (!error) {
          if (pageNumber === 0) {
            setLogs(auditData || []);
          } else {
            setLogs(prev => [...prev, ...auditData]);
          }
          
          if (auditData.length < ITEMS_PER_PAGE) {
            setHasMore(false);
          }
        }
      }
    } catch (err) {
      console.error("Audit Fetch Error:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchAuditLogs(0);
  }, [fetchAuditLogs]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchAuditLogs(nextPage);
  };

  const formatLogDate = (dateString) => {
    const d = new Date(dateString);
    // Napomena: Prilagodba vremena ovisi o tvom serveru, ovdje ostavljam tvoju logiku -1h
    const adjusted = new Date(d.getTime() - (1 * 60 * 60 * 1000));
    return {
      date: adjusted.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      time: adjusted.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    };
  };

  if (loading) return <div className="h-64 w-full bg-muted/20 animate-pulse rounded-[2.5rem] mt-16" />;

  return (
    <div className="w-full mt-4 animate-in fade-in duration-300">
      
      {/* BACK BUTTON */}
      <div className="flex justify-start mb-6">
        <button onClick={() => navigate(-1)} className="group flex items-center gap-2 text-muted-foreground hover:text-primary transition-all">
          <div className="p-2 rounded-xl bg-muted/30 group-hover:bg-primary/10 transition-colors">
            <Icon name="ArrowLeft" size={20} className="group-hover:-translate-x-1 transition-transform" />
          </div>
          <span className="text-[11px] font-black uppercase tracking-[0.2em]">{t('userLog.back')}</span>
        </button>
      </div>

      {/* NASLOV */}
      <div className="relative mb-10">
        <div className="flex flex-col relative z-10 bg-background pr-4 w-fit">
          <h2 className="text-2xl font-black uppercase tracking-tighter flex items-center gap-3 text-foreground/90">
            <div className="p-2 bg-primary rounded-lg text-white shadow-lg shadow-primary/20">
              <Icon name="Activity" size={20} />
            </div>
            {t('userLog.title')}
          </h2>
          <p className="text-xs text-muted-foreground font-semibold mt-1 italic opacity-80 tracking-wide ml-12">
            {t('userLog.subtitle')}
          </p>
        </div>
        <div className="absolute bottom-[-20px] left-0 w-full h-[1px] bg-gradient-to-r from-border via-border to-transparent" />
      </div>

      <div className="bg-card rounded-[2.5rem] border border-border/60 shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-y-auto max-h-[550px] custom-scrollbar">
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 z-20 bg-muted/90 backdrop-blur-md">
              <tr className="border-b border-border/50">
                <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">{t('userLog.table.time')}</th>
                <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">{t('userLog.table.user')}</th>
                <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">{t('userLog.table.action')}</th>
                <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">{t('userLog.table.document')}</th>
                <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground text-center">{t('userLog.table.device')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {logs.length > 0 ? (
                logs.map((log) => {
                  const formatted = formatLogDate(log.created_at);
                  
                  // Dinamičke boje za akcije
                  const getActionStyles = (action) => {
                    switch (action) {
                      case 'DOWNLOAD':
                        return 'bg-blue-500/10 text-blue-600';
                      case 'UPLOAD':
                        return 'bg-violet-500/10 text-violet-600';
                      case 'RESTORE':
                        return 'bg-emerald-500/10 text-emerald-600'; // ZELENA
                      case 'HARD DELETE':
                        return 'bg-red-500/10 text-red-600'; // CRVENA
                      default:
                        return 'bg-amber-500/10 text-amber-600';
                    }
                  };

                  return (
                    <tr key={log.id} className="hover:bg-primary/[0.02] transition-colors group">
                      <td className="px-6 py-5">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-foreground">{formatted.date}</span>
                          <span className="text-[10px] font-black text-primary uppercase tracking-tighter">{formatted.time}</span>
                        </div>
                      </td>

                      <td className="px-6 py-5">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <Icon name="User" size={12} className="text-primary" />
                            <span className="text-xs font-black uppercase tracking-tight text-foreground/80">
                              {log.executor_name || t('userLog.table.system')}
                            </span>
                          </div>
                          <span className="text-[11px] font-medium text-muted-foreground">{log.executor_email}</span>
                        </div>
                      </td>

                      <td className="px-6 py-5">
                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md ${getActionStyles(log.action)}`}>
                          {log.action}
                        </span>
                      </td>

                      <td className="px-6 py-5">
                        <div className="flex flex-col max-w-[200px]">
                          <span className="text-sm font-bold text-foreground truncate break-all">
                            {log.file_name || 'N/A'}
                          </span>
                          <span className="text-[10px] font-mono font-bold text-muted-foreground/60 uppercase">
                            ID: {log.file_id?.slice(0, 8) || '---'}
                          </span>
                        </div>
                      </td>

                      <td className="px-6 py-5 text-center">
                         <div className="flex flex-col items-center opacity-60 group-hover:opacity-100 transition-opacity">
                           <Icon name={log.user_agent?.includes('Mobile') ? "Smartphone" : "Monitor"} size={16} className="text-muted-foreground" />
                           <span className="text-[8px] font-black uppercase mt-1">
                              {log.ip_address === 'Admin Trash Panel' ? 'Admin App' : (log.ip_address === 'Client-side Request' ? 'Web App' : log.ip_address)}
                           </span>
                         </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-20 text-center text-muted-foreground opacity-30">
                    <Icon name="History" size={40} className="mx-auto mb-2" />
                    <p className="text-[10px] font-black uppercase tracking-widest">{t('userLog.table.noActivity')}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {hasMore && logs.length > 0 && (
          <div className="p-4 border-t border-border/40 flex justify-center bg-muted/5">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="flex items-center gap-2 px-6 py-2 rounded-2xl bg-background border border-border shadow-sm hover:border-primary/50 hover:text-primary transition-all disabled:opacity-50"
            >
              {loadingMore ? <Icon name="Loader2" size={16} className="animate-spin" /> : <><Icon name="ChevronDown" size={16} /> <span className="text-[10px] font-black uppercase tracking-widest">{t('userLog.loadMore')}</span></>}
            </button>
          </div>
        )}
        
        {/* FOOTER */}
        <div className="bg-muted/10 px-8 py-5 border-t border-border/50 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
             <div className="p-2 bg-background rounded-lg border border-border"><Icon name="ShieldCheck" size={14} className="text-primary" /></div>
             <p className="text-[9px] font-black uppercase tracking-[0.10em] text-muted-foreground max-w-md">{t('userLog.footer.gdprNote')}</p>
          </div>
          <div className="flex items-center gap-4 border-l pl-6 border-border/50">
             <div className="text-right">
                <p className="text-[8px] font-black uppercase text-muted-foreground/50 tracking-widest">{t('userLog.footer.policy')}</p>
                <p className="text-[10px] font-black text-primary uppercase">{t('userLog.footer.status')}</p>
             </div>
             <Icon name="Lock" size={24} className="text-primary/50" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogAuditUser;